import { parseMoneyToCents, tt, type MoneyInput, type PizzaDraft, type UpsellChoice } from '@agos/shared'

import type { CartLine } from './cart-math'
import { lineNoteOf } from './line-note'

/**
 * Adding to, changing and removing from the cart.
 *
 * Every one of these is addressed by **line index**, not by product id. Legacy
 * addresses them by product id, and the cart really can hold two lines with the
 * same id — the same pizza with different toppings, or the same drink ordered
 * in two kitchen rounds. Matching by id changes both (FB-053).
 *
 * `CartItemsList` in legacy already takes a `lineIndex` prop with a comment
 * explaining exactly this; `new-order.tsx` passes it and then ignores it.
 */

export interface OrderCartLine extends CartLine {
  isPizza?: boolean
  pizzaExtras?: string
  /** The surcharge already folded into `unitPrice`, so an edit can undo it. */
  extrasPriceCents?: number
  /**
   * Which toppings, and how many portions of each — §10A.80.
   *
   * `pizzaExtras` above is the *text*, which is what the kitchen ticket reads and what legacy
   * re-parses to reopen the dialog. This is what the line is **charged** from: the server prices
   * each ingredient from `pizza_ingredients` at the line's size, because §10A.9 re-reads every
   * catalogue price and would otherwise throw the surcharge away — which is exactly what it did,
   * silently, from the day §9.41 shipped.
   */
  extrasChosen?: ReadonlyArray<{ ingredientId: number; quantity: number }>
  boxGroupId?: string
  boxGroupName?: string
  boxSlotLabel?: string
  lunchMealType?: string
  /**
   * A line from the day's menu — §9.178, drawn as one box in the cart.
   *
   * **Client-only, and deliberately not `boxGroupId`.** That field travels to the server, is stored
   * on `order_items`, and means *remove as a unit*: a burger box is assembled as one and comes apart
   * as one. A day's menu is not — each meal was added on its own and has to be removable on its own.
   */
  lunchGroup?: boolean
  /** A line from an earlier kitchen round; shown with a note about what changing it does. */
  locked?: boolean
  /**
   * The server's id for a line that came off an existing order - §9.374.
   *
   * `order-edit.ts` has always set it; it lives on the type now because **`addProduct` reads it**:
   * merging an addition into a line the kitchen already has a ticket for hid the addition from the
   * edit payload, which is FB-668's *"Zatiaľ nič nepridané"*.
   */
  existingId?: number
  /**
   * What a composed line is assembled from — `BACKEND.md` §10A.14. A kombo's three pizzas, a custom
   * pizza's size, base and toppings. Absent on an ordinary catalogue line, which is most of them.
   */
  parts?: { productId?: number; name: string; unitPriceCents: number; quantity: number }[]
  /**
   * What a composed line **was** assembled from, read off an existing order - §10A.202, FB-748.
   *
   * **Deliberately not `parts` above, and the difference is money.** `itemOf` sends `parts` when a
   * line has them, and the server bounds a line that arrives with them (`boundedByParts`). A line
   * loaded for editing has not been re-composed by anybody, so re-sending its stored pieces would
   * re-price a bill a manager may have corrected by hand - which is `BLOCKED #12`'s open half, and
   * the reason a previous session built the edit-side bound and then reverted it. So the screen
   * reads this one and the payload builder never looks at it.
   */
  composedParts?: readonly { name: string; quantity: number }[]
  /**
   * Which rule bounds this composed line — `BACKEND.md` §10A.26.
   *
   * `'kombo'` means the dearest part rather than the sum, because a kombo is priced `max` by design
   * and the sum of three pizzas is far too loose a cap. Absent everywhere else, and a line without
   * it is bounded exactly as it was before.
   */
  composition?: 'kombo'
  /**
   * The draft a custom pizza **built in this košík** came from - `FRONTEND.md` §9.703, FB-1009.
   *
   * The builder resets to a fresh pizza the moment one is added, so without this a pizza added a minute ago
   * was in no builder and could not be opened again: `reopenablePizza` wanted a line off a saved order. Kept
   * on the line, the reopen puts this exact draft back - every portion count, the base and the pizza's own
   * name - with no name match that could refuse it. **Client-only**: the payload builders pick their fields
   * by name and never read it, so nothing new reaches the server.
   */
  pizzaDraft?: PizzaDraft
  /**
   * What somebody typed about this one line for the kitchen - §9.690, owner #3.
   *
   * Held as typed, so the box keeps its spaces while somebody writes; `lineNoteOf` trims it for
   * every reader. Never folded into `productName` or `customName`: that is what the receipt prints.
   */
  note?: string
  /**
   * This line's own identity, for the lines ordered with it to name - §9.713. **Client-only.**
   *
   * The cart addresses lines by index, which moves whenever a line above is removed, so a parent cannot be named by
   * its index. Set on a line the moment something is ordered with it (`nextLineId`, `withLineId`), and on a line
   * loaded from an order that has add-ons as `row:<order_items.id>` (`order-edit.ts`). Absent on every other line.
   */
  lineId?: string
  /**
   * The `lineId` of the line this one was ordered with - §9.713, owner O60/O76.
   *
   * Set only by `addAddonLines`. The payload turns it into the parent's index (`orderLinesPayload`), the košík draws
   * the line under its parent, and removing or stepping the parent takes it along (`removeLine`, `changeQuantity`).
   */
  addonOf?: string
  /**
   * §9.712, §9.713, `BACKEND.md` §10A.366 - the upsell group the add-on was chosen under, through the prompt.
   * `unitPrice` already carries the group's price (0 is *zadarmo*); this tells the server which group to re-price it
   * from and count against the per-unit limit. Read back as `upsellGroupId`; absent on every other line.
   */
  upsellGroupId?: number
  /**
   * The group's limit per unit of the parent - §9.713, answer #133. **Absent is no limit known** and nothing is
   * trimmed; a line loaded from an order never carries it, because the line reads do not answer it.
   */
  upsellMaxPerUnit?: number
}

export interface AddableProduct {
  id: number
  name: string
  price: MoneyInput
  categoryName?: string
  /** §9.85 — which of the product's sizes, priced by `BACKEND.md` §10A.33. */
  size?: string
  /**
   * §10A.14, for a composed line. A merge keeps the line already in the cart, which is right: two
   * lines only merge when they share an id, and a composed id is a hash of the line's own name — so
   * their parts are the same parts.
   */
  parts?: { productId?: number; name: string; unitPriceCents: number; quantity: number }[]
  /**
   * Which rule bounds this composed line — `BACKEND.md` §10A.26.
   *
   * `'kombo'` means the dearest part rather than the sum, because a kombo is priced `max` by design
   * and the sum of three pizzas is far too loose a cap. Absent everywhere else, and a line without
   * it is bounded exactly as it was before.
   */
  composition?: 'kombo'
  /** §9.703 - the custom pizza's own draft, so the line can be opened in the builder again. */
  pizzaDraft?: PizzaDraft
  /**
   * §9.724 - the note the line is created with: a reopened custom pizza's, which would otherwise come back without the
   * note the kitchen was told. A product carrying one never merges (`mergeTargetIndex`).
   */
  note?: string
}

/**
 * Adds one, merging into an existing line only when that line is genuinely the
 * same thing: same product, no box group, no toppings, not locked.
 *
 * A pizza that has been customised is its own line — merging it would silently
 * apply one customer's toppings to another's pizza.
 */
export function addProduct(
  cart: OrderCartLine[],
  product: AddableProduct,
  {
    isPizza = false,
    lineId,
  }: {
    isPizza?: boolean
    /**
     * §9.713 - the identity to give the line this add lands on, when it has none, so a prompt opened for it can name
     * it (`parentLineIdFor`). Absent changes nothing, which is every caller before §9.713.
     */
    lineId?: string
  } = {},
): OrderCartLine[] {
  const mergeableIndex = mergeTargetIndex(cart, product)
  const named = (line: OrderCartLine) =>
    lineId === undefined || line.lineId !== undefined ? line : { ...line, lineId }

  if (mergeableIndex >= 0) {
    return cart.map((line, index) =>
      index === mergeableIndex ? named({ ...line, quantity: line.quantity + 1 }) : line,
    )
  }

  return [
    ...cart,
    named({
      ...(product.size === undefined ? {} : { size: product.size }),
      productId: product.id,
      productName: product.name,
      unitPrice: product.price,
      quantity: 1,
      ...(isPizza ? { isPizza: true } : {}),
      ...(product.categoryName ? { categoryName: product.categoryName } : {}),
      ...(product.parts && product.parts.length > 0 ? { parts: product.parts } : {}),
      // §10A.26 — carried through so the kombo's `max` bound survives the basket.
      ...(product.composition ? { composition: product.composition } : {}),
      // §9.703 - a merge keeps the line already here, which is right: two lines merge only on the same id, and a
      // custom pizza's id is a hash of its name, which carries every count - so the two drafts are the same pizza.
      ...(product.pizzaDraft ? { pizzaDraft: product.pizzaDraft } : {}),
      // §9.724 - as typed, like every note on a line; `lineNoteOf` trims it for the readers.
      ...(lineNoteOf(product) === undefined ? {} : { note: product.note }),
    }),
  ]
}

/**
 * The line an add of this product would merge into, or -1 for a new line - `addProduct`'s own rule, named so the
 * screen can tell which line a prompt opened after the add belongs to (§9.713).
 */
export function mergeTargetIndex(cart: readonly OrderCartLine[], product: AddableProduct): number {
  // §9.85 — the size is part of *the same thing*. A 32 cm and a 50 cm of one pizza are two lines at
  // two prices; merging them would charge one of the two wrongly, whichever line won.
  // §9.374, FB-668 - **never into a line that came from the server.** The owner: *"when in previous
  // round they had kofola in košík, and I added new one, I received «Zatiaľ nič nepridané»."* A line
  // with an `existingId` is one the kitchen already has a ticket for; merging into it raised the
  // quantity on a line the edit payload did not look at, so the addition vanished and the guest
  // drank a kofola nobody was charged for. As its own line it travels as `newItems` and the bar gets
  // a round for it - which is better than legacy, where the merge makes it a silent correction.
  // §9.690, owner #3 - **never into a line that carries a note.** *Margherita - bez cibule* and a second
  // click on Margherita are two pizzas the kitchen makes differently; merging would put the note on
  // both, or - read the other way - lose the one pizza that was ordered plain.
  // §9.713 - **never into an add-on.** The fries ordered with burger Bacon are that burger's; a waiter tapping
  // *Hranolky* on their own is ordering fries nobody ordered with anything.
  return cart.findIndex(
    (line) =>
      line.productId === product.id &&
      line.size === product.size &&
      !line.boxGroupId &&
      !line.pizzaExtras &&
      !line.locked &&
      line.existingId === undefined &&
      line.addonOf === undefined &&
      lineNoteOf(line) === undefined &&
      // §9.724 - the same rule from the other side: a product that arrives with a note is never folded into a plain line.
      lineNoteOf(product) === undefined,
  )
}

/**
 * A fresh `lineId` for this cart - §9.713. `n1`, `n2`... one past the highest already given, so the same cart always
 * answers the same id: a state updater React runs twice computes the same line both times.
 */
export function nextLineId(cart: readonly OrderCartLine[]): string {
  const highest = cart.reduce((top, line) => {
    const match = /^n(\d+)$/.exec(line.lineId ?? '')
    return match === null ? top : Math.max(top, Number(match[1]))
  }, 0)
  return `n${highest + 1}`
}

/**
 * The `lineId` the line an add of this product lands on will carry - §9.713.
 *
 * The add-on prompt opens **after** the add (`addons.ts`'s rule), so the screen works out the parent's identity from
 * the cart it is adding to and hands it to `addProduct` as `lineId`: the line keeps an identity it already had, or
 * takes this one.
 */
export function parentLineIdFor(cart: readonly OrderCartLine[], product: AddableProduct): string {
  const target = cart[mergeTargetIndex(cart, product)]
  return target?.lineId ?? nextLineId(cart)
}

/**
 * The line at this index, given a `lineId` if it has none - §9.713, for a prompt opened over a line already in the
 * cart (the košík's "+", §9.712). Answers the cart unchanged when the line already has one.
 */
export function withLineId(
  cart: OrderCartLine[],
  lineIndex: number,
): { cart: OrderCartLine[]; lineId: string | null } {
  const target = cart[lineIndex]
  if (target === undefined) return { cart, lineId: null }
  if (target.lineId !== undefined) return { cart, lineId: target.lineId }
  const lineId = nextLineId(cart)
  return { cart: cart.map((line, index) => (index === lineIndex ? { ...line, lineId } : line)), lineId }
}

/** One add-on the prompt hands back - §9.712 builds it; `addAddonLines` puts it under its parent. */
export interface ChosenAddon {
  id: number
  name: string
  /** What the waiter was shown: the group's price where it sets one (0 is *zadarmo*), else the product's. */
  priceCents: number
  categoryName?: string
  upsellGroupId?: number | null
  /** The group's limit per unit of the parent; absent or null is no limit. */
  maxPerUnit?: number | null
}

/**
 * §9.712 + §9.713 - what the upsell prompt's confirm hands back, as `addAddonLines` takes it.
 *
 * The price is the one the waiter was shown (`unitPriceCents`: the group's where it sets one, 0 for *zadarmo*, else
 * the product's), and the group and its limit ride along so the line is re-priced and held by the server
 * (`BACKEND.md` §10A.366) and trimmed here when the parent is stepped down.
 */
export function chosenAddonsOf(choices: readonly UpsellChoice[]): ChosenAddon[] {
  return choices.map((choice) => ({
    id: choice.product.id,
    name: choice.product.name,
    priceCents: choice.unitPriceCents,
    ...(choice.product.categoryName === null ? {} : { categoryName: choice.product.categoryName }),
    upsellGroupId: choice.upsellGroupId,
    maxPerUnit: choice.maxPerUnit,
  }))
}

/**
 * §9.713 - the chosen add-ons as lines **of the line the prompt was opened for** (`parentLineId`).
 *
 * The same add-on chosen again for the same parent (a second burger, a second prompt) merges into its line, unless
 * that line is from an earlier round or carries a note - `addProduct`'s reasons. A parent that is not in the cart any
 * more, or is itself an add-on (the server refuses that link, `BACKEND.md` §10A.366), gets nothing linked: the
 * choices are added as ordinary lines, which is what every add-on was before §9.713.
 */
export function addAddonLines(
  cart: OrderCartLine[],
  parentLineId: string | null,
  chosen: readonly ChosenAddon[],
): OrderCartLine[] {
  const parent = parentLineId === null ? undefined : cart.find((line) => line.lineId === parentLineId)
  const linkable = parent !== undefined && parent.addonOf === undefined
  const added = chosen.reduce((cartSoFar, addon) => {
    const product = {
      id: addon.id,
      name: addon.name,
      price: addon.priceCents / 100,
      ...(addon.categoryName ? { categoryName: addon.categoryName } : {}),
    }
    if (!linkable) return addProduct(cartSoFar, product)
    const group = addon.upsellGroupId ?? undefined
    const same = cartSoFar.findIndex(
      (line) =>
        line.addonOf === parentLineId &&
        line.productId === addon.id &&
        line.upsellGroupId === group &&
        !line.locked &&
        line.existingId === undefined &&
        lineNoteOf(line) === undefined,
    )
    if (same >= 0) {
      return cartSoFar.map((line, index) => (index === same ? { ...line, quantity: line.quantity + 1 } : line))
    }
    return [
      ...cartSoFar,
      {
        productId: addon.id,
        productName: addon.name,
        unitPrice: product.price,
        quantity: 1,
        ...(addon.categoryName ? { categoryName: addon.categoryName } : {}),
        addonOf: parentLineId as string,
        ...(group === undefined ? {} : { upsellGroupId: group }),
        ...(group === undefined || addon.maxPerUnit == null ? {} : { upsellMaxPerUnit: addon.maxPerUnit }),
      },
    ]
  }, cart)
  return withinUpsellLimits(added)
}

/**
 * §9.713, answer #133 - a group's add-ons under one parent, held to the group's limit **per unit** of the parent.
 *
 * The customer basket's rule (`@agos/customer-core`'s `withinUpsellLimits`), over this cart's shape: for each parent
 * and group, the lines carrying `upsellMaxPerUnit` hold at most `upsellMaxPerUnit × parent quantity` between them;
 * over it, **the latest-added give way first** and a line trimmed to nought leaves. A line with no limit on it is never
 * trimmed - the server refuses more than a group allows (*K jednému kusu sa dá vybrať najviac N.*). Answers the same
 * array when nothing is over.
 */
export function withinUpsellLimits(cart: OrderCartLine[]): OrderCartLine[] {
  const units = new Map(
    cart
      .filter((line) => line.lineId !== undefined && line.addonOf === undefined)
      .map((line) => [line.lineId as string, line.quantity]),
  )
  const taken = new Map<string, number>()
  let changed = false
  const held = cart.flatMap((line) => {
    const parentUnits = line.addonOf === undefined ? undefined : units.get(line.addonOf)
    if (parentUnits === undefined || line.upsellMaxPerUnit === undefined || line.upsellGroupId === undefined) {
      return [line]
    }
    const group = `${line.upsellGroupId}<${line.addonOf}`
    const room = Math.max(0, line.upsellMaxPerUnit * parentUnits - (taken.get(group) ?? 0))
    const quantity = Math.min(line.quantity, room)
    taken.set(group, (taken.get(group) ?? 0) + quantity)
    if (quantity === line.quantity) return [line]
    changed = true
    return quantity === 0 ? [] : [{ ...line, quantity }]
  })
  return changed ? held : cart
}

/** An add-on whose parent has left the cart leaves too - it was only ever ordered with that line (§9.713). */
function withoutOrphans(cart: OrderCartLine[]): OrderCartLine[] {
  const parents = new Set(
    cart.filter((line) => line.lineId !== undefined && line.addonOf === undefined).map((line) => line.lineId),
  )
  const kept = cart.filter((line) => line.addonOf === undefined || parents.has(line.addonOf))
  return kept.length === cart.length ? cart : kept
}

/**
 * Steps one line's quantity, dropping it when it reaches zero.
 *
 * §9.713 - a parent dropped takes its add-ons with it, and a parent stepped down holds a group's add-ons to the
 * group's limit for the units left; an add-on stepped up past its limit is held there.
 */
export function changeQuantity(
  cart: OrderCartLine[],
  lineIndex: number,
  delta: number,
): OrderCartLine[] {
  const target = cart[lineIndex]
  // §9.374 - **`locked` no longer refuses.** The owner's example is three kofolas being poured and
  // the guest saying *"only two"*: that is a correction to an order in progress, not a refund, and
  // legacy's own replace branch names the case. A box group still refuses, because a slot of a kombo
  // is priced as part of the box rather than on its own.
  if (!target || target.boxGroupId) return cart

  return withinUpsellLimits(
    withoutOrphans(
      cart
        .map((line, index) =>
          index === lineIndex ? { ...line, quantity: line.quantity + delta } : line,
        )
        .filter((line) => line.quantity > 0),
    ),
  )
}

/** One line out - and, §9.713, the add-ons ordered with it. */
export function removeLine(cart: OrderCartLine[], lineIndex: number): OrderCartLine[] {
  const target = cart[lineIndex]
  // §9.374 - as above: *"it also happens when something is removed from košík"*. A removal is what
  // `items` exists to express.
  if (!target || target.boxGroupId) return cart
  return withoutOrphans(cart.filter((_line, index) => index !== lineIndex))
}

/** A box is assembled as a unit, so it is removed as one. */
/**
 * Add an assembled box as one group.
 *
 * Every line carries the same `boxGroupId`, which is what lets the cart remove the box as a unit and
 * what stops `addProduct` merging a box line into a loose one of the same product — the merge predicate
 * already excludes any line with a `boxGroupId`.
 */
export function addBoxLines(
  cart: OrderCartLine[],
  groupId: string,
  groupName: string,
  lines: readonly { slotLabel: string; product: AddableProduct }[],
): OrderCartLine[] {
  if (lines.length === 0) return cart
  return [
    ...cart,
    ...lines.map((line) => ({
      productId: line.product.id,
      productName: line.product.name,
      unitPrice: line.product.price,
      quantity: 1,
      ...(line.product.categoryName ? { categoryName: line.product.categoryName } : {}),
      boxGroupId: groupId,
      boxGroupName: groupName,
      boxSlotLabel: line.slotLabel,
    })),
  ]
}

export function removeBoxGroup(cart: OrderCartLine[], boxGroupId: string): OrderCartLine[] {
  return cart.filter((line) => line.boxGroupId !== boxGroupId)
}

/**
 * Replaces one line's toppings.
 *
 * The surcharge is folded into `unitPrice` — the API has nowhere else to put
 * it — so the previous one has to come off first or repeated edits accumulate.
 * `extrasPriceCents` is what remembers it.
 */
export function setLineExtras(
  cart: OrderCartLine[],
  lineIndex: number,
  extras: string,
  extrasPriceCents: number,
  chosen: ReadonlyArray<{ ingredientId: number; quantity: number }> = [],
): OrderCartLine[] {
  return cart.map((line, index) => {
    if (index !== lineIndex) return line

    const baseCents = parseMoneyToCents(line.unitPrice) - (line.extrasPriceCents ?? 0)
    return {
      ...line,
      pizzaExtras: extras || undefined,
      extrasPriceCents: extrasPriceCents || undefined,
      // §10A.80 — the ids travel so the server can price them. The default is empty rather than
      // required, so the existing callers and their tests keep their meaning.
      extrasChosen: chosen.length > 0 ? chosen : undefined,
      unitPrice: (baseCents + extrasPriceCents) / 100,
    }
  })
}

/**
 * A line's price before the toppings folded into it, in euros - `FRONTEND.md` §9.730.
 *
 * What the extras dialog quotes a new choice on top of, and exactly the figure `setLineExtras` keeps when it replaces
 * them. The dialog was handed `unitPrice`, which already carries the toppings, so reopening a pizza with ham quoted
 * the ham twice - and a saved pizza (§9.730) always opens with its toppings on.
 */
export function extrasBasePrice(
  line: Pick<OrderCartLine, 'unitPrice' | 'extrasPriceCents'> | undefined,
): number {
  if (line === undefined) return 0
  return (parseMoneyToCents(line.unitPrice) - (line.extrasPriceCents ?? 0)) / 100
}

/** How many of a product the cart holds, across every line that carries it. */
export function quantityOf(cart: OrderCartLine[], productId: number): number {
  return cart
    .filter((line) => line.productId === productId)
    .reduce((total, line) => total + line.quantity, 0)
}

/**
 * How many things are in the basket - the owner's item P3, §9.312.
 *
 * The owner asked for *"Košík (xx products)"*. **The sum of the quantities, not the number of lines**,
 * and the difference is the whole reason this is a function: three Margheritas on one line are three
 * products to whoever is reading the header and one line to the code. Legacy's own header counts
 * `cart.length` (`order-cart.tsx:83`), which would say *1* for a basket holding three pizzas.
 */
export function basketCount(cart: readonly OrderCartLine[]): number {
  return cart.reduce((total, line) => total + (line.quantity ?? 0), 0)
}

/**
 * `6 položiek`, with the plural Slovak actually uses.
 *
 * **One, two-to-four and five-or-more are three different words.** Legacy writes `položiek`
 * unconditionally, so its header reads *1 položiek* on a basket of one - wrong in a way the owner
 * reads every day. The rule is the standard Slovak one and is applied here rather than in the
 * component so it can be tested without rendering.
 */
export function basketCountLabel(count: number): string {
  if (count === 1) return `${count} ${tt('položka')}`
  if (count >= 2 && count <= 4) return `${count} ${tt('položky')}`
  return `${count} ${tt('položiek')}`
}

/**
 * The name the kitchen reads, toppings included - `FRONTEND.md` §9.420.
 *
 * **One expression, because two of them diverged twice.** The create path folded a customised
 * pizza's toppings into `customName` and the edit path's `itemOf` did not send the field at all, so
 * an edit that rewrote `items` turned *Margherita [bez cibule, extra syr]* back into *Margherita* -
 * the owner's *"note were missing after edit. It just disappear"* (FB-747). The first divergence
 * between these same two builders flattened a composed line's id to 0 and made a phoned pizza free.
 *
 * `customName` is not decoration: it is the only place a line's topping text is stored, so dropping
 * it loses what the customer asked for rather than merely how it is displayed.
 *
 * Takes the two fields it reads rather than a whole line, so a caller holding a loaded order's row
 * can use it as readily as one holding a cart line.
 */
export function lineDisplayName(line: {
  productName: string
  pizzaExtras?: string | undefined
}): string {
  return line.pizzaExtras ? `${line.productName} [${line.pizzaExtras}]` : line.productName
}
